import type { KnowledgeMetaSection } from '@cortex/domain-ac-mens-hub-api';
import { useQuery } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * Meta公式情報アーカイブの検索と全文取得。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta が公式に書いている原文を画面から引く。広告運用の判断を、公式に存在しない通説ではなく一次情報に基づかせる。
 * @graph-connects rpcClient [calls] knowledgeMeta 手続きの呼び出し
 */

/**
 * 一度に取得する検索結果の件数。
 * 収録12,176件に対し、実運用で見る「情報収集期間」で101件・「オーディエンスの断片化」で61件（2026-09-06 実測）。
 * 30件あれば上位を読み切れて、読み切れないほど出たときは絞り込みへ誘導する。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 検索結果を一度に読み込む件数。
 * @graph-connects none
 */
export const KNOWLEDGE_META_PAGE_SIZE = 30;

/**
 * キーワードで Meta公式情報を検索する。キーワードが空のときは実行しない
 * （全件は6,090万字あり、空検索を通すと意味のない負荷になる）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business キーワードと種類で Meta公式情報を検索する。
 * @graph-connects rpcClient [calls] knowledgeMeta.search
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useKnowledgeMetaSearch(keyword: string, section?: KnowledgeMetaSection) {
  return useQuery({
    queryKey: queryKeys.knowledgeMeta.search(keyword, section),
    queryFn: () =>
      rpcClient.knowledgeMeta.search({ keyword, section, limit: KNOWLEDGE_META_PAGE_SIZE }),
    enabled: keyword.length > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * 記事の全文を取得する。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta公式情報の記事1件を全文で取得する。
 * @graph-connects rpcClient [calls] knowledgeMeta.detail
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useKnowledgeMetaDetail(docId: string) {
  return useQuery({
    queryKey: queryKeys.knowledgeMeta.detail(docId),
    queryFn: () => rpcClient.knowledgeMeta.detail({ docId }),
    enabled: docId.length > 0,
    staleTime: 5 * 60_000,
  });
}
